import React, { useState } from 'react'
import { usePeople } from '../../hooks/usePeople'
import { useToast } from '../../hooks/useToast'
import { doc, writeBatch, collection, getDocs } from 'firebase/firestore'
import { db } from '../../firebase'

export default function ImportPage({ user, familyId }) {
  const { refetch } = usePeople(user, familyId)
  const { success, error, info } = useToast()
  const [file, setFile] = useState(null)
  const [importing, setImporting] = useState(false)
  const [preview, setPreview] = useState(null)
  const [stats, setStats] = useState(null)

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
      setStats(null)
      
      // Read and preview the file
      const reader = new FileReader()
      reader.onload = (event) => {
        const text = event.target.result
        setPreview(text)
        
        // Calculate stats
        const lines = text.trim().split('\n')
        const familyUnits = lines.filter(line => line.includes(' + ')).length
        const childLines = lines.filter(line => line.trim().startsWith('c:')).length
        
        setStats({
          totalLines: lines.length,
          familyUnits,
          childLines
        })
      }
      reader.readAsText(selectedFile)
    }
  }

  const parseFamilyTreeText = (text) => {
    const lines = text.trim().split('\n')
    const families = []
    let currentFamily = null

    for (let line of lines) {
      const trimmed = line.trim()
      
      // Skip empty lines
      if (!trimmed) {
        if (currentFamily) {
          families.push(currentFamily)
          currentFamily = null
        }
        continue
      }

      // Parse marriage line (e.g., "Person1 + Person2")
      if (line.includes(' + ') && !line.startsWith(' ')) {
        if (currentFamily) {
          families.push(currentFamily)
        }
        
        const [spouse1, spouse2] = line.split(' + ').map(s => s.trim())
        currentFamily = {
          spouse1,
          spouse2,
          children: []
        }
      }
      // Parse children line (e.g., " c: Child1, Child2, Child3")
      else if (trimmed.startsWith('c:')) {
        if (currentFamily) {
          const childrenStr = trimmed.substring(2).trim()
          const children = childrenStr
            .split(',')
            .map(c => c.trim())
            .filter(c => c && c !== '?')
          currentFamily.children.push(...children)
        }
      }
    }

    // Don't forget the last family
    if (currentFamily) {
      families.push(currentFamily)
    }

    return families
  }

  const handleImport = async () => {
    if (!file || !preview) {
      error('Please select a file first')
      return
    }

    setImporting(true)
    try {
      const families = parseFamilyTreeText(preview)
      
      if (families.length === 0) {
        error('No valid family data found in file')
        setImporting(false)
        return
      }

      // Step 1: Collect all unique person names
      const allNames = new Set()
      families.forEach(family => {
        allNames.add(family.spouse1)
        allNames.add(family.spouse2)
        family.children.forEach(child => allNames.add(child))
      })

      info(`Processing ${allNames.size} people and ${families.length} family units...`)

      // Step 2: Get existing people in ONE query
      const peopleRef = collection(db, 'familyTree', familyId, 'people')
      const existingSnap = await getDocs(peopleRef)
      const existingPeople = {}
      existingSnap.forEach(doc => {
        const data = doc.data()
        existingPeople[data.name] = { id: doc.id, ...data }
      })

      // Step 3: Create new people in ONE batch
      const batch = writeBatch(db)
      const nameToIdMap = {}
      let newPeopleCount = 0

      for (const name of allNames) {
        if (existingPeople[name]) {
          nameToIdMap[name] = existingPeople[name].id
        } else {
          const newDocRef = doc(peopleRef)
          nameToIdMap[name] = newDocRef.id
          batch.set(newDocRef, {
            name,
            spouses: [],
            children: [],
            parents: []
          })
          newPeopleCount++
        }
      }

      if (newPeopleCount > 0) {
        await batch.commit()
      }

      // Step 4: Fetch ALL people data (including newly created ones) in ONE query
      const allPeopleSnap = await getDocs(peopleRef)
      const allPersonData = {}
      allPeopleSnap.forEach(doc => {
        allPersonData[doc.id] = doc.data()
      })

      // Step 5: Build relationship updates for all families
      const updates = {}
      for (const id of Object.values(nameToIdMap)) {
        const data = allPersonData[id] || {}
        updates[id] = {
          spouses: new Set(data.spouses || []),
          children: new Set(data.children || []),
          parents: new Set(data.parents || [])
        }
      }

      // Process all families
      families.forEach(family => {
        const spouse1Id = nameToIdMap[family.spouse1]
        const spouse2Id = nameToIdMap[family.spouse2]
        const childrenIds = family.children.map(name => nameToIdMap[name]).filter(Boolean)

        if (spouse1Id && spouse2Id) {
          // Add spouses to each other
          updates[spouse1Id].spouses.add(spouse2Id)
          updates[spouse2Id].spouses.add(spouse1Id)

          // Add children to both parents
          childrenIds.forEach(childId => {
            updates[spouse1Id].children.add(childId)
            updates[spouse2Id].children.add(childId)
            
            // Add both parents to child
            if (updates[childId]) {
              updates[childId].parents.add(spouse1Id)
              updates[childId].parents.add(spouse2Id)
            }
          })
        }
      })

      // Step 6: Write all updates in ONE batch
      const updateBatch = writeBatch(db)
      for (const [id, data] of Object.entries(updates)) {
        const personRef = doc(db, 'familyTree', familyId, 'people', id)
        updateBatch.update(personRef, {
          spouses: Array.from(data.spouses),
          children: Array.from(data.children),
          parents: Array.from(data.parents)
        })
      }
      await updateBatch.commit()

      // Refresh the people list
      await refetch()

      // Show summary
      success(
        `Successfully imported ${newPeopleCount} new people and ${families.length} family units!`
      )

      // Reset form
      setFile(null)
      setPreview(null)
      setStats(null)
      if (document.getElementById('file-upload')) {
        document.getElementById('file-upload').value = ''
      }
      
    } catch (err) {
      console.error('Import error:', err)
      error('Failed to import: ' + err.message)
    } finally {
      setImporting(false)
    }
  }

  const handleClear = () => {
    setFile(null)
    setPreview(null)
    setStats(null)
    if (document.getElementById('file-upload')) {
      document.getElementById('file-upload').value = ''
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card p-6">
        <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Import Family Data
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Upload a file in the standard format to automatically create people and relationships.
        </p>
      </div>

      {/* File Upload */}
      <div className="glass-card p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Select File
            </label>
            <div className="flex gap-3">
              <input
                id="file-upload"
                type="file"
                accept=".txt"
                onChange={handleFileChange}
                disabled={importing}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-white
                         file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0
                         file:text-sm file:font-semibold file:bg-blue-500 file:text-white
                         hover:file:bg-blue-600 cursor-pointer disabled:opacity-50"
              />
              {file && (
                <button
                  onClick={handleClear}
                  disabled={importing}
                  className="glass-btn px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-red-500/20"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* File Stats */}
          {stats && (
            <div className="grid grid-cols-3 gap-4">
              <div className="glass-card p-4 text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {stats.totalLines}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Lines</div>
              </div>
              <div className="glass-card p-4 text-center">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {stats.familyUnits}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Family Units</div>
              </div>
              <div className="glass-card p-4 text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {stats.childLines}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Child Entries</div>
              </div>
            </div>
          )}

          {/* Import Button */}
          {file && (
            <button
              onClick={handleImport}
              disabled={importing}
              className="w-full glass-btn-primary py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {importing ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Importing...
                </span>
              ) : (
                'Import Family Data'
              )}
            </button>
          )}
        </div>
      </div>

      {/* Preview */}
      {preview && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
            File Preview
          </h3>
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 max-h-96 overflow-y-auto">
            <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono">
              {preview}
            </pre>
          </div>
        </div>
      )}

      {/* Format Documentation */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
          File Format
        </h3>
        <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
          <p>
            The file should follow this format:
          </p>
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <pre className="text-xs font-mono">
{`Parent1 + Parent2
 c: Child1, Child2, Child3

Parent3 + Parent4
 c: Child4, Child5`}
            </pre>
          </div>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Each marriage is on its own line: <code className="text-xs bg-gray-200 dark:bg-gray-700 px-1 rounded">Name1 + Name2</code></li>
            <li>Children are listed on the next line starting with a space and <code className="text-xs bg-gray-200 dark:bg-gray-700 px-1 rounded">c:</code></li>
            <li>Use <code className="text-xs bg-gray-200 dark:bg-gray-700 px-1 rounded">?</code> for unknown names (they will be skipped)</li>
            <li>Separate family units with blank lines</li>
            <li>The import will automatically create all people and link relationships</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
